import React, { Fragment, useCallback, useState } from 'react'
import { Stack, Typography, Grid, List, ListItem, ListItemAvatar, Chip, Avatar, Divider, TablePagination, Tooltip } from '@mui/material'
import { getFileTypeIconAsUrl } from '@fluentui/react-file-type-icons'
import InfoLinkPopper from '../../InfoLinkPopper.js';
import { formatBytes } from '../../utils.js';
import { getItemAnalyticsBatch, decodeSharePointText } from '../../data/index.js';
import FileActionIconButtons from './FileActionIconButtons.js';
import ResourcePreviewPopover from '../ResourcePreviewPopover.js';
import { textAlign } from '@mui/system';
import { alignProperty } from '@mui/material/styles/cssUtils.js';

function FileList(
  { categoryConfig, filter = undefined, 
    suppressFavorites = false, suppressTags = false, 
    onSelectTag = undefined, onDeleteDocument = undefined, onEditResourceProperties = undefined, onApproveDraft = undefined,
    pageNumberCallback = undefined, pageSizeCallback = undefined, isManager = false,
    driveConfig = undefined
  }) {

  const [filesAnalytics, setFilesAnalytics] = useState([])

  const resolveAnalytics = async (_files) => {
    const idList = (_files ?? categoryConfig.files).reduce((p,f) => {
      const etag = f.fields['@odata.etag']
      const sharepointId = etag?.replace(/"/g, '').split(',')[0] ?? undefined
      if (sharepointId) {
        return [...p, sharepointId]
      }
      return p
    }, [])
    if (idList.length) {
      const results = await getItemAnalyticsBatch(idList, 'allTime')
      setFilesAnalytics(results)
    } else setFilesAnalytics([])
  }

  const handleEditResourceProperties = useCallback((rId) => {
    if (onEditResourceProperties !== undefined) {
      onEditResourceProperties(rId, categoryConfig.category)
    }
  }, [onEditResourceProperties])

  const handleDeleteDocument = useCallback((rId, isPublished) => {
    if (onDeleteDocument !== undefined) {
      onDeleteDocument(rId, categoryConfig.category, isPublished)
    }
  }, [onDeleteDocument])

  const handleApproveDraft = useCallback((rId, category) => {
    if (onApproveDraft !== undefined) {
      onApproveDraft(rId, category ?? categoryConfig.category)
    }
  }, [onApproveDraft])

  // useEffect(() => {
  //   if (![undefined, null].includes(categoryConfig.files) && categoryConfig.files.length > 0) {
  //     // resolveAnalytics(categoryConfig.files)
  //   }

  // }, [categoryConfig.files])

  const getPage = (categoryConfig) => {
    let _files = categoryConfig.files
    if (categoryConfig.limit > -1) {
      const start = (categoryConfig.page - 1) * categoryConfig.limit + 1
      const end = Math.min(start + categoryConfig.limit - 1, _files.length)
      _files = _files.slice(start - 1, end)  
    }
    return _files
  }

  const fileList = getPage(categoryConfig)

  return <List sx={{ width: '100%', bgcolor: 'background.paper', padding: '1px 0 1px 0'}} dense disablePadding >
  {fileList.map( (file, idx, arr) => {
    
    const fileName = file?.fields?.LinkFilename
    const size = file?.fields?.FileSizeDisplay
    const title = file?.fields?.Title
    const description = decodeSharePointText(file?.driveItem?.description)
    const resourceTags = file?.fields?.QSEPortalTags
    const fileType = file?.fields?.DocIcon
    const createdByUser = file?.createdBy?.user ?? file?.driveItem?.createdBy?.user
    const createdDate = file?.createdDateTime        
    const sharepointId = (file?.fields['@odata.etag'] ?? '').replace(/"/g, '').split(',')[0]
    const analytics = filesAnalytics.find(a => a.id === sharepointId)

    const handleSelectTag = (tag) => {
      if (!suppressTags && onSelectTag !== undefined) {
        if (filter) {
          const _t = filter.split(',')
          if (! _t.includes(tag)) {
            tag = [..._t, tag].join(',')
          }
        }
        onSelectTag(tag)
      }
    }

    return (
      <Fragment key={idx}>
      <ListItem alignItems='center' key={idx} sx={{padding: '1px 0 1px 0'}}>
        <ResourcePreviewPopover 
            icon={
              <ListItemAvatar sx={{ justifyItems: 'center', padding: '4px 4px 0 4px', margin: '0'}}>
                <Avatar sx={{ width: 28, height: 28 }} src={getFileTypeIconAsUrl({extension: `.${(fileType ?? '').toLowerCase()}`})} variant='square' />  
                <InfoLinkPopper title={fileType ? fileType.toUpperCase() : 'Info'} key={`info_${idx}`} typography={{ fontSize: '0.6rem !important'}} content={
                  <Stack rowGap={.5} >
                    <Typography >File Name: {fileName} </Typography>
                    <Typography >Size: {formatBytes(size)} </Typography>
                    <Typography >Added by {createdByUser.displayName} on {new Date(createdDate).toLocaleDateString()}</Typography>
                    { analytics && analytics.body?.access && 
                      <Typography >Accessed by {analytics.body?.access.actorCount} Users</Typography>
                    }
                  </Stack>
                  } />
              </ListItemAvatar>
            }
            file={file} key={file?.driveItem?.id} triggerLabel='Open a Preview' driveConfig={driveConfig}
          />
        <Grid container alignItems='center' >
            <Grid item sm={12} md={3.5} >
              <Typography variant="body2" color="text.primary" fontWeight='bold'  component='div'>
                  {title ?? fileName}
                </Typography>
            </Grid>
            <Grid item sm={12} md={8.5}>
              <Grid container>
                <Grid item xs={12} paddingBottom='4px'>
                  <Typography variant='body2' color='text.secondary' whiteSpace='pre-wrap'>
                    {description}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container columnGap={0.25}  alignItems='end' paddingBottom='2px' justifyContent='space-between'>
                <Grid item sm={12} lg={7.5} paddingBottom='4px' >
                  <Stack gap={0.5} direction='row' justifyItems='flex-end' flexWrap='wrap'>
                  { resourceTags && resourceTags.split(',').sort((a,b) => a.localeCompare(b)).map((tag) => {
                    const tagLabel = tag.replace(/tag:/,'')
                    return (
                      <Tooltip key={tag} arrow placement='left' title={`Click to filter for ${tagLabel}`} disableHoverListener={suppressTags}>
                      <Chip label={tagLabel} size='small'
                        sx={{'& .MuiChip-label': {
                          fontSize: '0.7rem'
                        }
                        }}
                      clickable={!suppressTags}
                        onClick={() => { handleSelectTag(tag)}}                         
                      />
                      </Tooltip>
                    )}
                  )}
                  </Stack>
                </Grid>
                <Grid item sm={12} lg={4.25} textAlign='right' 
                  sx={{textWrap: 'nowrap', '& .MuiIconButton-root': {
                        padding: '4px'
                      }
                  }}>
                    <FileActionIconButtons 
                      file={file} 
                      category={categoryConfig.category} 
                      suppressFavorites={suppressFavorites} 
                      onEditResourceProperties={handleEditResourceProperties} 
                      onApproveDraft={handleApproveDraft}
                      onDeleteDocument={handleDeleteDocument}
                      isManager={isManager}
                      driveConfig={driveConfig} />
                </Grid>
                
              </Grid>

            </Grid>
          </Grid>
        </ListItem>
        
      {/* {idx !== (arr.length-1) &&  */}
      <Divider variant="inset" component="li"  key={`divider_${idx}`} sx={{ marginLeft: 'unset' }} />
      {/* } */}
    </Fragment>
    
  )}
  )}
    <ListItem disableGutters sx={{justifyContent: 'center'}} disablePadding>
      <TablePagination size='small' component='div'
        slotProps={{
          actions: {
            nextButton: { size: 'small', sx: { padding: '2px'}},
            previousButton: { size: 'small', sx: { padding: '2px'}}
          },
          select: { 
            autoComplete: 'false',
            size: 'small'
          }
        }}
        rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
        count={categoryConfig.files.length} 
        page={categoryConfig.page - 1}
        rowsPerPage={categoryConfig.limit}
        onPageChange={(event, page) => {
          if (pageNumberCallback !== undefined) {
            pageNumberCallback(categoryConfig.category, page)
          }
        }}
        onRowsPerPageChange={(event) => {
          if (pageSizeCallback !== undefined) {
            pageSizeCallback(categoryConfig.category, parseInt(event.target.value, 10))
          }
        }}
        sx={
          { borderBottom: 'unset',
            '& .MuiTablePagination-toolbar': { minHeight: 'unset', paddingLeft: 'unset', paddingTop: '3px', paddingBottom: '3px'},
            '& .MuiTablePagination-toolbar p': { minHeight: 'unset', marginBottom: 'unset' },
            '& .MuiTablePagination-displayedRows': {
              typography: 'caption'
            },
            '& .MuiTablePagination-selectLabel': {
              typography: 'caption'
            },
            '& .MuiTablePagination-select': {
              typography: 'caption'
            },
            '& .MuiTablePagination-menuItem': {
              typography: 'caption'
            },
            '& .MuiTablePagination-actions': {
              padding: 'unset'
            }
          }}
      />
    </ListItem>
  </List>
}

export default React.memo(FileList)